import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPortalApplications, downloadApplication } from "../../services/api";
import Table from "../../utils/Table";
import ActionButtons from "../../utils/ActionButtons";
import { getStatusDot, formatDate } from "../../utils/TableUtils";
import ApplicationModal from "../../components/Modals/ApplicationModal";
import { useToast } from "../../contexts/ToastContext";
import { useLoading } from "../../contexts/LoadingContext";
import Dropdown from "react-bootstrap/Dropdown";
import downloadIcon from "../../assets/images/icons/download.svg";
import { useAuth } from "../../contexts/AuthContext";

function PortalDashboard() {
  const { userDetail } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [filterInput, setFilterInput] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  const navigate = useNavigate();
  const pageSize = 10;

  const handleView = (applicationData) => {
    navigate(`/portal/application/${applicationData.applicantId}`);
  };

  const handleDownload = useCallback(async (rowData) => {
    try {
      const { applicantId } = rowData;
      if (!applicantId) {
        showToast("Invalid ID, please try again.", "error");
        return;
      }
      startLoading();
      await downloadApplication(applicantId);
      showToast("Download started successfully.", "success");
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("An error occurred while downloading the application.", "error");
      }
    } finally {
      stopLoading();
    }
  }, [showToast, startLoading, stopLoading]);

  const handleEdit = (applicationData) => {
    setSelectedApplication(applicationData);
    setModalShow(true);
  };

  const refreshAppData = () => {
    setModalShow(false);
    setSelectedApplication(null);
    fetchData();
  };

  const handlePageChange = (newPageIndex) => {
    setPageIndex(newPageIndex);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "Upload Date",
        accessor: "date",
        Cell: ({ value }) => formatDate(value),
        sortType: (rowA, rowB) => {
          const a = rowA.values.date?.toLowerCase() || "";
          const b = rowB.values.date?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Application #",
        accessor: "applicantId",
        Cell: ({ value }) => value?.toString().padStart(2, "0"),
        sortType: (rowA, rowB) => {
          const a = rowA.values.applicantId?.toString().toLowerCase() || "";
          const b = rowB.values.applicantId?.toString().toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Applicant Name",
        accessor: row => `${row.firstName} ${row.lastName}`,
        Cell: ({ row }) => `${row.original.firstName} ${row.original.lastName}`,
        sortType: (rowA, rowB) => {
          const a = rowA.values.firstName?.toLowerCase() || "";
          const b = rowB.values.firstName?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Address",
        accessor: row => {
          const address = row?.location?.address || "";
          return address.replace(/,\s*(United States|United States of America|USA|U\.S\.A\.|US|U\.S\.|America)\s*$/i, "").trim();
        },
        Cell: ({ row }) => {
          const address = row.original.location?.address || "";
          return address.replace(/,\s*(United States|United States of America|USA|U\.S\.A\.|US|U\.S\.|America)\s*$/i, "").trim();
        },
        sortType: (rowA, rowB) => {
          const a = rowA.values.location?.address?.toLowerCase() || "";
          const b = rowB.values.location?.address?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => getStatusDot(value),
        sortType: (rowA, rowB) => {
          const a = rowA.values.status?.toLowerCase() || "";
          const b = rowB.values.status?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Attachments",
        accessor: "location.files",
        disableSortBy: true,
        Cell: ({ row }) => {
          const files = row.original.location?.files || [];
          return files.length > 0 ? (
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                size="sm"
                id={`dropdown-${row.original.applicantId}`}
                className="btn-action d-flex align-items-center gap-2 border"
              >
                <img src={downloadIcon} alt="Files" className="action-icon" style={{ width: '16px' }} />
                <span className="action-label fw-bold">Files ({files.length})</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {files.map((file, index) => (
                  <Dropdown.Item
                    key={index}
                    href={file.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-flex align-items-center gap-2"
                  >
                    <img src={downloadIcon} alt="File" style={{ width: '14px', opacity: 0.7 }} />
                    <span>File {index + 1}</span>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <span className="text-muted">None</span>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "actions",
        id: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <ActionButtons
            onEdit={handleEdit}
            onView={handleView}
            onDelete={() => { }}
            onDownload={handleDownload}
            rowData={row.original}
            showDelete={false}
            showEdit={row.original.isEditable}
            additonalBtn={true}
            viewBtn={true}
          />
        ),
      },
    ];

    return baseColumns;
  }, [handleDownload]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchPortalApplications(pageIndex + 1, pageSize);
      if (response.success) {
        setData(response.data);
        setPageCount(response.pagination.totalPages);
        setTotalCount(response.pagination.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching data:", error?.response?.data?.message);
      showToast("Failed to load applications.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  return (
    <div className="subMain-content">
      <div className="page-header">
        <h1 className="page-title">My Applications</h1>
        {totalCount > 0 && (
          <p className="page-subtitle">
            {totalCount} {totalCount === 1 ? 'application' : 'applications'} found
          </p>
        )}
      </div>
      <Table
        columns={columns}
        data={data}
        filterInput={filterInput}
        setFilterInput={setFilterInput}
        onPageChange={handlePageChange}
        currentPage={pageIndex}
        pageCount={pageCount}
        loading={loading}
        handleEdit={handleEdit}
      />
      {selectedApplication && (
        <ApplicationModal
          show={modalShow}
          data={selectedApplication}
          onHide={refreshAppData}
        />
      )}
    </div>
  );
}

export default PortalDashboard;
